import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { setToken } from '../actions/auth';
import { createUser, isUsernameAvailable, getOpenBootcamps } from '../actions/api';
import AuthLayout, { PasswordRules, passwordIsValid, apiErrorMessage } from './AuthLayout';

const formatRange = (b) => {
  if (!b.start_date) return '';
  const opts = { month: 'short', day: 'numeric' };
  const start = new Date(b.start_date).toLocaleDateString(undefined, opts);
  const end = b.end_date ? new Date(b.end_date).toLocaleDateString(undefined, opts) : '';
  return end ? `${start} – ${end}` : start;
};

const Register = () => {
  const [form, setForm] = useState({ firstname: '', lastname: '', email: '', username: '', password: '', bootcampId: '' });
  const [bootcamps, setBootcamps] = useState(null);
  const [usernameTaken, setUsernameTaken] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getOpenBootcamps().then(setBootcamps).catch(() => setBootcamps([]));
  }, []);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (key === 'username') setUsernameTaken(false);
  };

  const checkUsername = async () => {
    if (form.username.trim().length < 3) return;
    try {
      setUsernameTaken(!(await isUsernameAvailable(form.username.trim())));
    } catch (e) {
      // The server checks again on submit.
    }
  };

  const ready = form.firstname && form.lastname && form.email && form.username.trim().length >= 3
    && form.bootcampId && passwordIsValid(form.password) && !usernameTaken;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await setToken(await createUser(form));
    } catch (err) {
      setError(apiErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} noValidate>
        <h2>Create your account</h2>
        <p className="lede">You’ll use this to log customers during bootcamp. It also sets up your ATNVT login.</p>
        {error && <div className="form-error" role="alert">{error}</div>}

        <TextField select label="Your bootcamp" value={form.bootcampId} onChange={set('bootcampId')} required
          disabled={bootcamps !== null && bootcamps.length === 0}
          helperText={bootcamps && bootcamps.length === 0 ? 'No bootcamps are open for sign-up right now. Check with your trainer.' : ' '}>
          {(bootcamps || []).map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}{formatRange(b) && <span style={{ color: '#5B6670', marginLeft: 8 }}>{formatRange(b)}</span>}
            </MenuItem>
          ))}
        </TextField>
        <div style={{ display: 'flex', gap: 12 }}>
          <TextField label="First name" autoComplete="given-name" value={form.firstname} onChange={set('firstname')} required />
          <TextField label="Last name" autoComplete="family-name" value={form.lastname} onChange={set('lastname')} required />
        </div>
        <TextField label="Email" type="email" autoComplete="email" value={form.email} onChange={set('email')} required />
        <TextField label="Username" autoComplete="username" value={form.username} onChange={set('username')}
          onBlur={checkUsername} required error={usernameTaken}
          helperText={usernameTaken ? 'That username is taken. Try another.' : 'At least 3 characters.'} />
        <TextField label="Password" type="password" autoComplete="new-password" value={form.password}
          onChange={set('password')} required />
        <PasswordRules password={form.password} />

        <Button type="submit" variant="contained" size="large" fullWidth disabled={busy || !ready}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
        <div className="auth-links">
          <span>Already registered? <Link to="/">Log in</Link></span>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Register;
